import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import "./BDNForm.scss";

import { AbsDropDown, Label, TextArea } from "abs-ui-components";
import { Input } from "abs-ui-components";

import Loader from "../../../../../common/Loader/Loader";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import axiosHttp from "../../../../../services/interceptor";
import * as _ from "lodash";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { preventScientificNotation } from "../../../../../services/utilities";

dayjs.extend(utc);
dayjs.extend(timezone);

const BDNForm = ({
	onClose,
	editBDN,
	type,
	onRefetchBDNAndMisStateMentErrs,
	setDisableBtn,
	bdn_data,
}) => {
	const reportDetails = useSelector(
		(state) => state.reports.selectedReportDetails
	);

	const {
		control,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm();
	const formValues = watch();
	const [loading, setLoading] = useState(false);
	const [disableAdd, setDisableAdd] = useState(false);

	const initialFormValues = useMemo(() => {
		return editBDN || {}; // If `editBDN` data exists, use it; otherwise, use an empty object
	}, [editBDN]);

	const handleCancel = () => {
		onClose();
	};

	const sanitizeKey = (key) => {
		if (!key) return "";
		return key.replace(/\|/g, "").replace(/\./g, "_");
	};


	const transformedOptions = bdn_data?.sections?.flatMap((section) =>
		section.fields
			.filter((field) => field.dataType === "options")
			.map((field) => ({
				fieldName: field.fieldName,
				key: field.key,
				options: (field.options || []).map((option) =>
					typeof option === "string"
						? { label: option, value: option }
						: option
				),
			}))
	);

	useEffect(() => {
		const hasChanged = Object.keys(formValues).some((key) => {
			return formValues[key] !== initialFormValues[key];
		});
		setDisableAdd(!hasChanged); // Enables button if form has changed, disables if unchanged
	}, [formValues, initialFormValues]);

	const transformOptions = (options = []) => {
		return options?.map((option) => ({
			label: option,
			value: option,
		}));
	};

	const handleBDNFormSubmit = async (formData) => {
		let fuel_fields = [];
		const fuel_section = bdn_data?.sections?.find(
			(item) => item.sectionName === "Fuels Section"
		);

		fuel_section?.fields?.forEach((fuel) => {
			fuel_fields.push(sanitizeKey(fuel.key));
		});
		let metadata = {};

		Object.entries(formData).map(([fieldKey, fieldValue]) => {
			if (fuel_fields?.indexOf(fieldKey) !== -1) {
				let index = fuel_fields?.indexOf(fieldKey);
				// metadata[`${fuel_section?.fields[index]?.key}`] = fieldValue;
				metadata[`${fuel_section?.fields[index]?.key}`] =
					fieldValue !== undefined && fieldValue !== ""
						? parseFloat(fieldValue) // Parse as number if valid value is given
						: null; // Set to null if value is missing or empty
			}
		});

		let bdnPayload = {
			voyageBdnDtlId: bdn_data.voyageBdnDtlId || 0,
			reportMstId: +reportDetails?.ReportMstId,
			bdnType: formData?.BDNTYPE?.value,
			dateOfOperation: formData?.DATE
				? dayjs(formData.DATE).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
				: null,
			remarks: formData?.REMARKS || "",
			metadata: metadata,
		};

		if (type === "Add") {
			axiosHttp
				.post(`api/VoyageBdn/AddMepcVoyageBdn`, bdnPayload)
				.then((res) => {
					if (res && res?.data?.isSuccess) {
						setLoading(false);
						onClose();
						emitToasterMsg("success", "BDN", "Added Successfully");
						setDisableBtn(false);
						onRefetchBDNAndMisStateMentErrs();
					} else {
						setLoading(false);
						setDisableBtn(false);
					}
				})
				.catch((error) => {
					setLoading(false);
					setDisableBtn(false);
					console.error(error);
				});
		} else {
			await axiosHttp({
				url: `api/VoyageBdn/EditMepcVoyageBdn?voyageBdnDtlId=${bdn_data?.voyageBdnDtlId}`,
				method: "POST",
				data: bdnPayload,
			})
				.then((res) => {
					if (res && res?.data?.isSuccess) {
						setLoading(false);
						onClose();
						setDisableBtn(false);
						emitToasterMsg(
							"success",
							"BDN",
							"Updated Successfully"
						);
						onRefetchBDNAndMisStateMentErrs();
					} else {
						setLoading(false);
						setDisableBtn(false);
					}
				})
				.catch((error) => {
					setLoading(false);
					setDisableBtn(false);
					console.error(error);
				});
		}
	};

	return (
		<>
			{loading && <Loader />}
			<form
				onSubmit={handleSubmit(handleBDNFormSubmit)}
				className="bdn-form-container"
			>
				{bdn_data && (
					<>
						{bdn_data?.sections?.map((section, sectionIndex) => (
							<div
								key={sectionIndex}
								className="section-container pr-3"
							>
								{section.sectionName === "Main Section" &&
									section?.fields?.map(
										(formField, fieldIndex) => (
											<>
												<MDBRow
													key={fieldIndex}
													className="mt-3"
												>
													<MDBCol
														md="6"
														className="input-div-height"
													>
														{formField.dataType ===
															"date" && (
																<>
																	<Label
																		bold
																		name={
																			formField.fieldName
																		}
																		className="mb-1"
																	/>
																	<Controller
																		name={
																			sanitizeKey(formField.key)
																		}
																		control={
																			control
																		}
																		rules={{
																			required:
																				"Please enter a date",
																		}}
																		defaultValue={
																			type ===
																				"Edit" &&
																				dayjs(
																					`${formField.value} GMT`
																				).isValid()
																				? dayjs(
																					`${formField.value} GMT`
																				)
																				: null
																		}
																		render={({
																			field,
																		}) => {
																			return (
																				<LocalizationProvider
																					dateAdapter={
																						AdapterDayjs
																					}
																				>
																					<DateTimePicker
																						timezone="UTC"
																						format="DD/MMM/YYYY hh:mm"
																						label="Select Date Time"
																						value={
																							field.value ||
																							null
																						}
																						onChange={(
																							newValue
																						) =>
																							field.onChange(
																								newValue
																							)
																						}
																						renderInput={(
																							params
																						) => (
																							<Input
																								{...params}
																							/>
																						)}
																					/>
																					{errors[
																						formField
																							.key
																					] && (
																							<div className="pt-2 error">
																								{
																									errors[
																										formField
																											.key
																									]
																										?.message
																								}
																							</div>
																						)}
																				</LocalizationProvider>
																			);
																		}}
																	/>
																</>
															)}
													</MDBCol>
												</MDBRow>

												<MDBRow className="mt-2">
													<MDBCol
														key={fieldIndex}
														md="6"
													>
														{formField?.dataType ===
															"options" &&
															formField?.key && (
																<>
																	<Label
																		bold
																		name={
																			formField.fieldName
																		}
																		className="mb-1"
																	/>
																	<Controller
																		name={
																			sanitizeKey(formField.key)
																		}
																		control={
																			control
																		}
																		rules={{
																			required:
																				"Please select",
																		}}
																		defaultValue={
																			type ===
																				"Edit"
																				? {
																					field: formField.value,
																					value: formField.value,
																				}
																				: transformOptions
																		}
																		render={({
																			field,
																		}) => {
																			return (
																				<>
																					<AbsDropDown
																						options={transformOptions(
																							formField.options
																						)}
																						value={
																							field?.value
																						}
																						placeholder="Select"
																						selected={
																							field
																								.value
																								?.value
																						}
																						handleChange={(
																							e
																						) => {
																							field.onChange(
																								e
																							);
																						}}
																					/>
																				</>
																			);
																		}}
																	/>
																	{errors[
																		formField
																			.key
																	] && (
																			<div className="pt-2 error">
																				{
																					errors[
																						formField
																							.key
																					]
																						?.message
																				}
																			</div>
																		)}
																</>
															)}
													</MDBCol>
												</MDBRow>
											</>
										)
									)}

								{section.sectionName === "Fuels Section" && (
									<MDBRow className="mt-4">
										{section?.fields?.map(
											(formField, fieldIndex) => (
												<MDBCol
													md="3"
													className="pr-2 mt-3"
													key={formField.key}
												>
													{formField.dataType ===
														"decimal" && (
															<>
																<Label
																	bold
																	name={
																		formField.fieldName
																	}
																	className="mb-1"
																/>
																<Controller
															name={sanitizeKey(formField.key)}
															control={control}
															defaultValue={type === "Edit" ? formField.value || "" : ""}
															render={({ field }) => {
																if (!field) return null;
																const bdnTypeValue = watch("BDNTYPE");
																const selectedBDNType = typeof bdnTypeValue === "string" ? bdnTypeValue : bdnTypeValue?.value;
																const isNegativeAllowed = selectedBDNType === "COR";
																return (
																	<Input
																		{...field}
																		className="inputHeight"
																		type="text"
                                                                        inputMode="decimal"
																		value={field?.value ?? ""}
																		onChange={(e) => {
																			const inputValue = typeof e === "object" && e?.target ? e.target.value : e;
																			let value = inputValue ?? "";
																			value = value.replace(/[^0-9.-]/g, "");
																			if (isNegativeAllowed) {
																				const hasLeadingMinus = value.startsWith("-");
																				value = value.replace(/-/g, "");
																				if (hasLeadingMinus) {
																					value = "-" + value;
																				}
																			} else {
	                                                                              value = value.replace(/-/g, "");
																			}
																			const parts = value.split(".");
																			if (parts.length > 2) {
																				value = parts[0] + "." + parts.slice(1).join("");
																			}
																			field.onChange(value);
																		}}
																		onKeyDown={(e) => {
																			if (e.key === "ArrowUp" || e.key === "ArrowDown") {
																				e.preventDefault();
																			}
																			if (e.key === "-") {
																				const currentValue = field.value || "";
																				if (
																					!isNegativeAllowed ||
																					currentValue.includes("-") ||
																					e.target.selectionStart !== 0
																				) {
																					e.preventDefault();
																				}
																			}
																			if (!/[0-9.-]/.test(e.key) && e.key.length === 1) {
																				e.preventDefault();
																			}
																		}}
																		onWheel={(e) => e.target.blur()}
																	/>
																);
															}}
														/>
															</>
														)}
												</MDBCol>
											)
										)}
									</MDBRow>
								)}

								{section.sectionName === "Remarks Section" &&
									section?.fields?.map(
										(formField, fieldIndex) => (
											<MDBRow
												key={fieldIndex}
												className="mt-4"
											>
												<MDBCol
													md="12"
													className="input-div-height"
												>
													{formField.dataType ===
														"string" && (
															<>
																<Label
																	bold
																	name={
																		formField.fieldName
																	}
																	className="mb-1"
																/>
																<Controller
																	name={
																		sanitizeKey(formField.key)
																	}
																	control={
																		control
																	}
																	defaultValue={
																		type ===
																			"Edit"
																			? formField.value ||
																			null
																			: ""
																	}
																	render={({
																		field,
																	}) => (
																		<TextArea
																			className="textarea-resize"
																			value={
																				field.value
																			}
																			onChange={
																				field.onChange
																			}
																		/>
																	)}
																/>
															</>
														)}
												</MDBCol>
											</MDBRow>
										)
									)}
							</div>
						))}

						<div className="d-flex justify-content-end mr-3 center-buttons">
							<button
								className="clear-button mr-4"
								onClick={handleCancel}
							>
								Cancel
							</button>

							<button
								type="submit"
								className={`save-button ${disableAdd ? "dimmed" : ""
									}`}
								disabled={disableAdd}
							>
								{type === "Add" ? "Add" : "Update"}
							</button>
						</div>
					</>
				)}
			</form>
		</>
	);
};

export default BDNForm;
